import React, { useState, useMemo } from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatINR, formatIndianNumber } from '../../../utils/currency';

const FinancialAnalysisPanel = ({ warrantyData = [] }) => {
  const [selectedPeriod, setSelectedPeriod] = useState(12); // 6, 12, 24 months
  const [selectedView, setSelectedView] = useState('cost-projection'); // 'cost-projection' | 'vendor-breakdown'

  const COLORS = ['#2563EB', '#059669', '#D97706', '#DC2626', '#7C3AED', '#0EA5E9', '#EC4899', '#14B8A6'];

  // 1. Dynamic Financial Metrics from Live Assets
  const financialSummary = useMemo(() => {
    let totalCoveredValue = 0;
    let totalExpiringCost = 0;
    let expiringCount = 0;
    const now = new Date();

    warrantyData.forEach(asset => {
      const price = asset.costRaw || 0;
      totalCoveredValue += price;

      if (asset.warrantyEndDate) {
        const expDate = new Date(asset.warrantyEndDate);
        const daysDiff = (expDate - now) / (1000 * 60 * 60 * 24);
        if (daysDiff >= 0 && daysDiff <= (selectedPeriod * 31)) {
          expiringCount++;
          totalExpiringCost += price;
        }
      }
    });

    // Realistic warranty renewal is typically ~15% of initial hardware capital expenditure
    const estimatedRenewalCost = Math.round(totalExpiringCost * 0.15);
    // Potential capital savings by renewing instead of buying whole new units
    const potentialSavings = Math.max(0, totalExpiringCost - estimatedRenewalCost);
    const savingsRate = totalExpiringCost > 0 ? (potentialSavings / totalExpiringCost) * 100 : 0;

    return {
      totalCoveredValue,
      totalExpiringCost,
      estimatedRenewalCost,
      potentialSavings,
      savingsRate,
      expiringCount,
      totalAssets: warrantyData.length
    };
  }, [warrantyData, selectedPeriod]);

  // 2. Dynamic Monthly Cost Projections
  const monthlyProjections = useMemo(() => {
    const months = {};
    const now = new Date();

    for (let i = 0; i < selectedPeriod; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const key = d.toLocaleString('en-IN', { month: 'short', year: '2-digit' });
      months[key] = {
        month: key,
        replacementCost: 0,
        renewalCost: 0,
        assetCount: 0
      };
    }

    warrantyData.forEach(asset => {
      if (!asset.warrantyEndDate) return;
      const date = new Date(asset.warrantyEndDate);
      const daysDiff = (date - now) / (1000 * 60 * 60 * 24);

      if (daysDiff < 0 || daysDiff > (selectedPeriod * 31)) return;

      const key = date.toLocaleString('en-IN', { month: 'short', year: '2-digit' });
      if (months[key]) {
        const price = asset.costRaw || 0;
        months[key].assetCount++;
        months[key].replacementCost += price;
        months[key].renewalCost += Math.round(price * 0.15);
      }
    });

    return Object.values(months);
  }, [warrantyData, selectedPeriod]);

  // 3. Dynamic Vendor Breakdown
  const vendorBreakdown = useMemo(() => {
    const vendorMap = {};

    warrantyData.forEach(asset => {
      const v = asset.vendor || 'Direct / OEM';
      if (!vendorMap[v]) {
        vendorMap[v] = {
          name: v,
          totalValue: 0,
          renewalCost: 0,
          count: 0
        };
      }
      const price = asset.costRaw || 0;
      vendorMap[v].totalValue += price;
      vendorMap[v].renewalCost += Math.round(price * 0.15);
      vendorMap[v].count++;
    });

    const totalAll = Object.values(vendorMap).reduce((s, item) => s + item.totalValue, 0) || 1;

    return Object.values(vendorMap)
      .map((item, idx) => ({
        ...item,
        percentage: ((item.totalValue / totalAll) * 100).toFixed(1),
        color: COLORS[idx % COLORS.length]
      }))
      .sort((a, b) => b.totalValue - a.totalValue);
  }, [warrantyData]);

  const CustomProjectionTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload;
      return (
        <div className="bg-popover border border-border rounded-lg shadow-elevated p-3 text-xs">
          <p className="font-bold text-popover-foreground mb-1.5">{label}</p>
          <div className="space-y-1">
            <div className="flex justify-between space-x-4">
              <span className="text-muted-foreground">Assets Expiring:</span>
              <span className="font-bold text-foreground">{data.assetCount}</span>
            </div>
            <div className="flex justify-between space-x-4">
              <span className="text-muted-foreground">Estimated Renewal Cost (~15%):</span>
              <span className="font-bold text-primary">{formatINR(data.renewalCost, false)}</span>
            </div>
            <div className="flex justify-between space-x-4">
              <span className="text-muted-foreground">Full Replacement Value:</span>
              <span className="font-bold text-error">{formatINR(data.replacementCost, false)}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Financial KPI Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-5 shadow-subtle">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Est. Renewal Budget ({selectedPeriod}M)
            </span>
            <div className="p-2 bg-primary/10 text-primary rounded-lg">
              <Icon name="IndianRupee" size={18} />
            </div>
          </div>
          <div className="text-2xl font-bold text-primary">
            {formatINR(financialSummary.estimatedRenewalCost, false)}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Projected for {financialSummary.expiringCount} expiring assets
          </p>
        </div>

        <div className="bg-card border border-border rounded-lg p-5 shadow-subtle">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Full Replacement Value
            </span>
            <div className="p-2 bg-error/10 text-error rounded-lg">
              <Icon name="RefreshCw" size={18} />
            </div>
          </div>
          <div className="text-2xl font-bold text-foreground">
            {formatINR(financialSummary.totalExpiringCost, false)}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Cost if replaced with new hardware
          </p>
        </div>

        <div className="bg-card border border-border rounded-lg p-5 shadow-subtle">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Potential Capital Savings
            </span>
            <div className="p-2 bg-success/10 text-success rounded-lg">
              <Icon name="TrendingUp" size={18} />
            </div>
          </div>
          <div className="text-2xl font-bold text-success">
            {formatINR(financialSummary.potentialSavings, false)}
          </div>
          <p className="text-xs text-success font-semibold mt-1">
            {financialSummary.savingsRate.toFixed(1)}% savings vs new procurement
          </p>
        </div>

        <div className="bg-card border border-border rounded-lg p-5 shadow-subtle">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Total Portfolio Valuation
            </span>
            <div className="p-2 bg-accent/10 text-accent rounded-lg">
              <Icon name="ShieldCheck" size={18} />
            </div>
          </div>
          <div className="text-2xl font-bold text-foreground">
            {formatINR(financialSummary.totalCoveredValue, true)}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Across {warrantyData.length} tracked enterprise assets
          </p>
        </div>
      </div>

      {/* Main Analysis Chart Section */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-subtle">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">
              {selectedView === 'cost-projection' ? 'Renewal vs Replacement Cost Projection' : 'Vendor Warranty Valuation Breakdown'}
            </h3>
            <p className="text-xs text-muted-foreground">
              {selectedView === 'cost-projection'
                ? `Monthly budget projection for upcoming ${selectedPeriod} months in Indian Rupees (₹)`
                : 'Asset portfolio value and renewal allocation distributed by vendor'}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* View Selector */}
            <div className="inline-flex rounded-md border border-border p-0.5 bg-muted/40">
              <button
                onClick={() => setSelectedView('cost-projection')}
                className={`px-3 py-1 text-xs font-medium rounded transition-smooth ${
                  selectedView === 'cost-projection' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Cost Projection
              </button>
              <button
                onClick={() => setSelectedView('vendor-breakdown')}
                className={`px-3 py-1 text-xs font-medium rounded transition-smooth ${
                  selectedView === 'vendor-breakdown' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                By Vendor
              </button>
            </div>

            {/* Period Selector */}
            {selectedView === 'cost-projection' && (
              <div className="inline-flex rounded-md border border-border p-0.5 bg-muted/40">
                <button
                  onClick={() => setSelectedPeriod(6)}
                  className={`px-2.5 py-1 text-xs font-medium rounded ${
                    selectedPeriod === 6 ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'
                  }`}
                >
                  6M
                </button>
                <button
                  onClick={() => setSelectedPeriod(12)}
                  className={`px-2.5 py-1 text-xs font-medium rounded ${
                    selectedPeriod === 12 ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'
                  }`}
                >
                  12M
                </button>
                <button
                  onClick={() => setSelectedPeriod(24)}
                  className={`px-2.5 py-1 text-xs font-medium rounded ${
                    selectedPeriod === 24 ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'
                  }`}
                >
                  24M
                </button>
              </div>
            )}
          </div>
        </div>

        {selectedView === 'cost-projection' ? (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyProjections} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" stroke="#64748B" fontSize={11} />
                <YAxis stroke="#64748B" fontSize={11} tickFormatter={(val) => `₹${(val / 1000).toFixed(0)}k`} />
                <Tooltip content={<CustomProjectionTooltip />} />
                <Legend />
                <Bar dataKey="renewalCost" name="Est. Renewal Cost (₹)" fill="#2563EB" radius={[4, 4, 0, 0]} />
                <Bar dataKey="replacementCost" name="Full Replacement (₹)" fill="#EF4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={vendorBreakdown}
                    dataKey="totalValue"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={105}
                    innerRadius={45}
                  >
                    {vendorBreakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(val) => [formatINR(val, false), 'Portfolio Value']}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Vendor List */}
            <div className="space-y-2.5 max-h-80 overflow-y-auto pr-1">
              <h4 className="font-semibold text-xs text-muted-foreground uppercase tracking-wide mb-2">
                Vendor Expenditure Ranking
              </h4>
              {vendorBreakdown.map((item) => (
                <div key={item.name} className="p-2.5 rounded-lg border border-border bg-muted/20 text-xs">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center space-x-2 truncate">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                      <span className="font-semibold text-foreground truncate" title={item.name}>{item.name}</span>
                    </div>
                    <span className="font-bold text-foreground">{item.percentage}%</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground pt-1 border-t border-border/40">
                    <span>{item.count} Assets</span>
                    <span className="font-semibold text-foreground">{formatINR(item.totalValue, true)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Strategic Decision Matrix */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-subtle">
        <h4 className="font-semibold text-sm text-card-foreground mb-3 flex items-center space-x-2">
          <Icon name="Lightbulb" size={16} className="text-warning" />
          <span>Strategic Procurement Recommendations</span>
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-muted-foreground">
          <div className="p-3.5 rounded-lg bg-success/5 border border-success/20">
            <div className="font-bold text-success text-sm mb-1">Proactive Renewal Action</div>
            <p>
              Renewing warranties for assets in the 30–60 day window secures OEM volume pricing and avoids out-of-warranty inspection surcharges.
            </p>
          </div>
          <div className="p-3.5 rounded-lg bg-warning/5 border border-warning/20">
            <div className="font-bold text-warning text-sm mb-1">High-Risk Hardware</div>
            <p>
              Critical server and networking gear approaching end-of-life should be prioritized for comprehensive SLAs to minimize downtime risks.
            </p>
          </div>
          <div className="p-3.5 rounded-lg bg-primary/5 border border-primary/20">
            <div className="font-bold text-primary text-sm mb-1">Vendor Contract Consolidation</div>
            <p>
              Consolidating multiple asset renewal dates with primary vendors like Apple, Dell, or HP can unlock enterprise volume discounts of up to 10-15%.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialAnalysisPanel;
